import React, { useState, useEffect, useCallback } from "react";
import { Form, Button, Card, Row, Col, Table } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch, faRedo, faFilter, faChevronLeft,
    faChevronRight, faUserPen, faCalendarAlt
} from "@fortawesome/free-solid-svg-icons";
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import FormatBulletedListIcon from '@mui/icons-material/FormatListBulleted';
import Select from 'react-select';
import Skeleton from 'react-loading-skeleton';
import PaginatedItems from '../../../../components/administracao/Pagination';
import useFetch from "../../../../hooks/useFetch";
import ImagemPadrao from '../../../../assets/no_image.jpg';

// Importação do SCSS
import '../../../../scss/visitante/iconografiaPublico.scss';

const API_URL = process.env.REACT_APP_API_URL;

const OPCOES_PROCEDENCIA = ["Doação", "Compra", "Produção própria", "Outro"];

export default function BuscarIconografia() {
    // Em telas pequenas o painel de filtros começa recolhido: assim o visitante
    // vê os resultados primeiro e abre os filtros quando quiser (em telas
    // grandes continua aberto, como antes).
    const [sidebarExpanded, setSidebarExpanded] = useState(
        () => typeof window === "undefined" || window.innerWidth >= 992
    );
    const [displayFormat, setDisplayFormat] = useState("card");
    const { get } = useFetch();

    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname]);

    const [state, setState] = useState({
        itens: [],
        count: 0,
        loading: true,
        nome_pesquisa: "",
        ordem: "Alfabetica", // padrão: acervo ordenado por título (A-Z), igual ao acervo de itens
        ano_inicial: "",
        ano_final: "",
        decada_inicial: null,
        decada_final: null,
        categorias: [],
        procedencia: null,
        local: "",
        pessoa: null,
        evento: null,
        autor: null,
        acervo: null,
    });

    const [opcoes, setOpcoes] = useState({ categorias: [], autores: [], eventos: [], pessoas: [], acervos: [] });

    const optionsDecadas = [
        { value: "1910", label: "1910-1919" }, { value: "1920", label: "1920-1929" },
        { value: "1930", label: "1930-1939" }, { value: "1940", label: "1940-1949" },
        { value: "1950", label: "1950-1959" }, { value: "1960", label: "1960-1969" },
        { value: "1970", label: "1970-1979" }, { value: "1980", label: "1980-1989" },
        { value: "1990", label: "1990-1999" }, { value: "2000", label: "2000-2009" },
        { value: "2010", label: "2010-2019" }, { value: "2020", label: "2020-2029" },
    ];

    const optionsProcedencia = OPCOES_PROCEDENCIA.map((v) => ({ value: v, label: v }));

    const carregarOpcoes = useCallback(async () => {
        const [categoriasRes, autoresRes, eventosRes, pessoasRes, acervosRes] = await Promise.all([
            get("/categoriafotografia?page=1&pageSize=1000"),
            get("/autorfotografo?page=1&pageSize=1000"),
            get("/eventodocumentado?page=1&pageSize=1000"),
            get("/pessoaretratada?page=1&pageSize=1000"),
            get("/acervodetentor?page=1&pageSize=1000"),
        ]);
        setOpcoes({
            categorias: categoriasRes.ok ? (categoriasRes.result.data ?? []).map((c) => ({ value: c.id_categoria, label: c.nome_categoria })) : [],
            autores: autoresRes.ok ? (autoresRes.result.data ?? []).map((a) => ({ value: a.id_autor, label: a.nome })) : [],
            eventos: eventosRes.ok ? (eventosRes.result.data ?? []).map((e) => ({ value: e.id_evento, label: e.nome_evento })) : [],
            pessoas: pessoasRes.ok ? (pessoasRes.result.data ?? []).map((p) => ({ value: p.id_pessoa, label: p.nome })) : [],
            acervos: acervosRes.ok ? (acervosRes.result.data ?? []).map((p) => ({ value: p.id_acervo_detentor, label: p.nome })) : [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function montarQuery(s, page) {
        let params = new URLSearchParams();
        if (s.nome_pesquisa) params.set("nome_pesquisa", s.nome_pesquisa);
        if (s.ano_inicial) params.set("ano_inicial", s.ano_inicial);
        if (s.ano_final) params.set("ano_final", s.ano_final);
        if (s.decada_inicial) params.set("decada_inicial", s.decada_inicial.value);
        if (s.decada_final) params.set("decada_final", s.decada_final.value);
        if (s.categorias && s.categorias.length > 0) params.set("categorias", s.categorias.map((c) => c.value).join(","));
        if (s.procedencia) params.set("procedencia", s.procedencia.value);
        if (s.local) params.set("local", s.local);
        if (s.pessoa) params.set("pessoa", s.pessoa.value);
        if (s.evento) params.set("evento", s.evento.value);
        if (s.autor) params.set("autor", s.autor.value);
        if (s.acervo) params.set("acervo", s.acervo.value);
        if (s.ordem) params.set("ordem", s.ordem);
        params.set("page", page ?? 1);
        return params.toString();
    }

    async function buscar(s, page) {
        setState((prev) => ({ ...prev, loading: true }));
        try {
            let response = await get(`/acervo/fotografias?${montarQuery(s, page)}`);
            if (response.ok) {
                setState((prev) => ({ ...prev, itens: response.result.data, count: response.result.count, loading: false }));
            } else {
                setState((prev) => ({ ...prev, itens: [], count: 0, loading: false }));
            }
        } catch (error) {
            console.error("Erro no buscar():", error);
            setState((prev) => ({ ...prev, itens: [], count: 0, loading: false }));
        }
    }

    useEffect(() => {
        window.scrollTo(0, 0);
        carregarOpcoes();
        buscar(state, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const executarBusca = () => buscar(state, 1);

    const limparFiltros = () => {
        const limpo = {
            ...state, nome_pesquisa: "", ano_inicial: "", ano_final: "",
            decada_inicial: null, decada_final: null, categorias: [],
            procedencia: null, local: "", pessoa: null, evento: null, autor: null,
            acervo: null,
        };
        setState(limpo);
        buscar(limpo, 1);
    };

    // Passa o novo valor explicitamente pro buscar() em vez de confiar no
    // `state` logo após o setState (setState não é síncrono, então o
    // `state` ainda estaria com o valor antigo de `ordem`).
    const handleOrderChange = (event) => {
        const novoEstado = { ...state, ordem: event.target.value };
        setState(novoEstado);
        buscar(novoEstado, 1);
    };

    const handlePageChange = (pageNumber) => {
        window.scrollTo(0, 0);
        buscar(state, pageNumber);
    };

    const toggleSidebar = () => setSidebarExpanded(!sidebarExpanded);

    const customStylesSelectUni = {
        option: (provided, stateProps) => ({
            ...provided,
            color: stateProps.isSelected ? "#fff" : "#495057",
            backgroundColor: stateProps.isSelected ? "#987272" : stateProps.isFocused ? "rgba(152, 114, 114, 0.1)" : "white",
            fontSize: '14px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: "#dee2e6",
            borderRadius: "6px",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            minHeight: '38px',
            '&:hover': { borderColor: "#987272" },
        }),
        singleValue: (provided) => ({ ...provided, color: "#495057" }),
        multiValue: (provided) => ({ ...provided, backgroundColor: "rgba(152, 114, 114, 0.15)" }),
        multiValueLabel: (provided) => ({ ...provided, color: "#987272" }),
        placeholder: (provided) => ({ ...provided, color: "#987272", fontSize: '14px' }),
        menu: (provided) => ({ ...provided, zIndex: 5 }),
        // menuPortal: o menu é renderizado no <body> (menuPortalTarget abaixo),
        // então não é mais cortado pelo card/modal em que o Select está nem
        // aparece por baixo da navbar. z-index alinhado com scss/base/responsivo.scss.
        menuPortal: (base) => ({ ...base, zIndex: 1080 }),
    };

    return (
        <div className="acervo-iconografico-page">
            <section className="search-hero">
                <div className="container">
                    <div className="search-modern-container text-center">
                        <h2 className="search-hero-title">Explore o Acervo Iconográfico</h2>
                        <p className="search-hero-subtitle">Fotografias e registros históricos que marcaram a trajetória da FMRP-USP.</p>

                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10">
                                <div className="d-flex flex-column flex-md-row gap-3 align-items-stretch">
                                    <div className="flex-grow-1 position-relative">
                                        <FontAwesomeIcon icon={faSearch} className="search-icon-modern" />
                                        <Form.Control
                                            type="text"
                                            className="search-input-modern w-100"
                                            value={state.nome_pesquisa}
                                            onChange={(e) => setState({ ...state, nome_pesquisa: e.target.value })}
                                            onKeyDown={(e) => e.key === "Enter" && executarBusca()}
                                            style={{ height: '48px' }}
                                        />
                                    </div>
                                    <div className="d-flex gap-2 search-modern-buttons">
                                        <Button className="btn-search-modern" onClick={executarBusca}>
                                            <FontAwesomeIcon icon={faFilter} className="me-2" />
                                            Aplicar filtros e Buscar
                                        </Button>
                                        <Button className="btn-reset-modern" onClick={limparFiltros}>
                                            <FontAwesomeIcon icon={faRedo} className="me-2" />
                                            <span className="d-none d-sm-inline">Redefinir</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="container-fluid py-5">
                <Row className="mx-0">
                    <Col lg={sidebarExpanded ? 3 : 1} md={sidebarExpanded ? 3 : 1} className={`transition-sidebar ${!sidebarExpanded ? 'sidebar-collapsed-container' : ''}`}>
                        {sidebarExpanded ? (
                            <div className="sidebar-modern animate-in">
                                <div className="d-flex align-items-center justify-content-between mb-4 p-3 pb-0">
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faFilter} style={{ color: '#987272' }} />
                                        <h5 className="sidebar-title mb-0">Filtros Adicionais</h5>
                                    </div>
                                    <button className="filter-button-modern" onClick={toggleSidebar}>
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                </div>

                                <div className="p-3 pt-0">
                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Categorias</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} isMulti options={opcoes.categorias} placeholder="Selecionar categorias..." styles={customStylesSelectUni} isClearable
                                                value={state.categorias} onChange={(op) => setState({ ...state, categorias: op ?? [] })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Procedência</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} options={optionsProcedencia} placeholder="Selecionar procedência..." styles={customStylesSelectUni} isClearable
                                                value={state.procedencia} onChange={(op) => setState({ ...state, procedencia: op })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Local</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Form.Control className="input-moderno" placeholder="Filtrar por local..."
                                                value={state.local} onChange={(e) => setState({ ...state, local: e.target.value })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Pessoa Retratada</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} options={opcoes.pessoas} placeholder="Pesquisar nome..." styles={customStylesSelectUni} isClearable
                                                value={state.pessoa} onChange={(op) => setState({ ...state, pessoa: op })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Evento</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} options={opcoes.eventos} placeholder="Evento histórico..." styles={customStylesSelectUni} isClearable
                                                value={state.evento} onChange={(op) => setState({ ...state, evento: op })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Autor / Fotógrafo</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} options={opcoes.autores} placeholder="Ex: Marc Ferrez" styles={customStylesSelectUni} isClearable
                                                value={state.autor} onChange={(op) => setState({ ...state, autor: op })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Acervo</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Select menuPortalTarget={document.body} options={opcoes.acervos} placeholder="Ex: Departamento..." styles={customStylesSelectUni} isClearable
                                                value={state.acervo} onChange={(op) => setState({ ...state, acervo: op })} />
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Intervalo de Anos</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Row className="g-2">
                                                <Col xs={6}>
                                                    <Form.Label className="label-admin-secondary">De (Ano)</Form.Label>
                                                    <Form.Control type="number" placeholder="1910" className="input-moderno"
                                                        value={state.ano_inicial} onChange={(e) => setState({ ...state, ano_inicial: e.target.value })} />
                                                </Col>
                                                <Col xs={6}>
                                                    <Form.Label className="label-admin-secondary">Até (Ano)</Form.Label>
                                                    <Form.Control type="number" placeholder="1950" className="input-moderno"
                                                        value={state.ano_final} onChange={(e) => setState({ ...state, ano_final: e.target.value })} />
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="filter-card mb-3">
                                        <Card.Header className="filter-card-header"><Card.Title className="filter-card-title">Período (Décadas)</Card.Title></Card.Header>
                                        <Card.Body className="filter-card-content">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="label-admin-secondary">Década Inicial</Form.Label>
                                                <Select menuPortalTarget={document.body} placeholder="Selecionar década inic..." options={optionsDecadas} styles={customStylesSelectUni}
                                                    value={state.decada_inicial} onChange={(opt) => setState({ ...state, decada_inicial: opt })} isClearable />
                                            </Form.Group>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="label-admin-secondary">Década Final</Form.Label>
                                                <Select menuPortalTarget={document.body} placeholder="Selecionar década fin..." options={optionsDecadas} styles={customStylesSelectUni}
                                                    value={state.decada_final} onChange={(opt) => setState({ ...state, decada_final: opt })} isClearable />
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>

                                    <div className="d-grid gap-2 mt-4">
                                        <Button className="btn-filter-primary" onClick={executarBusca}>Aplicar Filtros</Button>
                                        <Button variant="outline-secondary" className="btn-filter-outline" onClick={limparFiltros}>Limpar Filtros</Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="sidebar-collapsed text-center">
                                <button className="filter-button-modern mt-3" onClick={toggleSidebar}>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                                <div className="mt-2">
                                    <small style={{ color: '#987272', writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: '600' }}>
                                        Filtros
                                    </small>
                                </div>
                            </div>
                        )}
                    </Col>

                    <Col lg={sidebarExpanded ? 9 : 11} md={sidebarExpanded ? 8 : 11}>
                        <div className="row d-flex flex-row justify-content-between" style={{ marginBottom: "30px" }}>
                            <div className="col-md-4 d-flex flex-row align-items-center mb-3">
                                <p className="mb-0 mr-2 text-filter">Ordenar por: </p>
                                <Form className="flex-grow-1">
                                    <Form.Select className="form-control" name="ordem" value={state.ordem} onChange={handleOrderChange} style={{ minWidth: "200px" }}>
                                        <option value="Padrao">Padrão</option>
                                        <option value="Alfabetica">Ordem alfabética (A-Z)</option>
                                    </Form.Select>
                                </Form>
                            </div>
                            <div className="col d-flex justify-content-end mb-3">
                                {displayFormat === "card" ? (
                                    <>
                                        <Button className="btn border border-2 btn-view" onClick={() => setDisplayFormat("card")}>
                                            <SpaceDashboardIcon />
                                        </Button>
                                        <button className="btn border border-2 btn-display-format" onClick={() => setDisplayFormat("table")}>
                                            <FormatBulletedListIcon className="icon-display-format" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="btn border border-2 btn-display-format" onClick={() => setDisplayFormat("card")}>
                                            <SpaceDashboardIcon className="icon-display-format" />
                                        </button>
                                        <Button className="btn border border-2 btn-view" onClick={() => setDisplayFormat("table")}>
                                            <FormatBulletedListIcon />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        {displayFormat === "card" ? (
                            <Row className="g-4">
                                {state.loading ? (
                                    Array(6).fill(0).map((_, i) => (
                                        <Col key={i} md={6} lg={4} xl={3}><Skeleton height={350} borderRadius={12} /></Col>
                                    ))
                                ) : state.itens.length > 0 ? (
                                    state.itens.map(item => (
                                        <Col key={item.id_fotografia} md={6} lg={4} xl={3}>
                                            <Link to={`/acervo/iconografia/${item.id_fotografia}`} className="text-decoration-none">
                                                <Card className="photo-result-card h-100 border-0 shadow-sm">
                                                    <div className="photo-container">
                                                        <Card.Img variant="top" src={`${API_URL}/acervo/fotografias/${item.id_fotografia}/imagem`} alt={item.titulo_atribuido} onError={(e) => { e.target.style.display = 'none'; }} />
                                                        <div className="tombo-badge">{item.numero_tombo}</div>
                                                    </div>
                                                    <Card.Body className="d-flex flex-column">
                                                        <Card.Title className="photo-title-main mb-2">{item.titulo_atribuido}</Card.Title>
                                                        <div className="photo-info-row d-flex align-items-center flex-wrap mb-2">
                                                            <div className="me-3">
                                                                <FontAwesomeIcon icon={faUserPen} className="me-2" />
                                                                {item.AutorFotografo?.nome ?? "Não identificado"}
                                                            </div>
                                                            <div>
                                                                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                                                {item.ano_producao ?? "-"}
                                                            </div>
                                                        </div>
                                                        <p className="photo-summary mt-auto">
                                                            {(item.descricao ?? "").length > 85 ? item.descricao.substring(0, 85) + "..." : (item.descricao ?? "")}
                                                        </p>
                                                    </Card.Body>
                                                </Card>
                                            </Link>
                                        </Col>
                                    ))
                                ) : (
                                    <Col xs={12}>
                                        <div className="text-center py-5 bg-white rounded-4 shadow-sm">
                                            <p className="text-muted mb-0">Nenhuma fotografia encontrada com esses filtros.</p>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        ) : (
                            /* VISUALIZAÇÃO EM TABELA */
                            <Table className="table table-hover" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th scope="col" style={{ width: "20%" }}>Foto</th>
                                        <th scope="col" style={{ width: "25%" }}>Título</th>
                                        <th scope="col" style={{ width: "20%" }}>Autor</th>
                                        <th scope="col" style={{ width: "20%" }}>Resumo</th>
                                        <th scope="col" style={{ width: "15%" }}>Ano</th>
                                    </tr>
                                </thead>
                                <tbody className="pt-2">
                                    {state.loading ? (
                                        Array(5).fill(0).map((_, index) => (
                                            <tr key={index}>
                                                <td><Skeleton height={80} width={120} /></td>
                                                <td><Skeleton width="90%" /></td>
                                                <td><Skeleton width="90%" /></td>
                                                <td><Skeleton width="90%" /></td>
                                                <td><Skeleton width="50%" /></td>
                                            </tr>
                                        ))
                                    ) : state.itens.length > 0 ? (
                                        state.itens.map(item => (
                                            <tr key={item.id_fotografia} style={{ cursor: 'pointer' }} onClick={() => window.location.href = `/acervo/iconografia/${item.id_fotografia}`}>
                                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                                    {/* Tamanho controlado 100% pela classe .img-wrapper-tabela
                                                        (buscar.scss), mesmo padrão do acervo de itens — sem style
                                                        inline aqui pra não brigar com o width/height/object-fit
                                                        definidos lá e deixar a miniatura preencher todo o box. */}
                                                    <div className="img-wrapper-tabela">
                                                        <img
                                                            src={`${API_URL}/acervo/fotografias/${item.id_fotografia}/imagem`}
                                                            alt={item.titulo_atribuido}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = ImagemPadrao; }}
                                                        />
                                                    </div>
                                                </td>
                                                <td>{item.titulo_atribuido}</td>
                                                <td>{item.AutorFotografo?.nome ?? "Não identificado"}</td>
                                                <td>{(item.descricao ?? "").length > 60 ? item.descricao.substring(0, 60) + "..." : (item.descricao ?? "")}</td>
                                                <td>{item.ano_producao ?? "-"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center">Nenhuma fotografia encontrada</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        )}

                        {state.itens.length > 0 && (
                            <div className="pt-5 d-flex justify-content-center">
                                <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </div>
    );
}
